import React, { useMemo } from 'react';
import posthog from 'posthog-js';
import { utils } from '@makeship/core';
import Image from 'next/image';
import styled from 'styled-components';
import Link from 'next/link';
import { Caption, S3Styles } from '../Typography';
import config from '../../../config.json';
import { ExperimentVariant, getExperimentFlags } from '../../context/flagging';
import { useStore } from '../../store';
import {
  isApparelCollectionHandle,
  isPlushieCollectionHandle,
  isTopPicksCollectionHandle,
} from '../../utils/collections';
import { isUserLoggedIn } from '../../utils/analytics';
import HorizontalScroller from '../HorizontalScroller/HorizontalScroller';
import { experimentKeys } from '../../utils/flagging';

const { plushCollectionHandles, apparelCollectionHandles } = config;

const SidebarWrapper = styled.div<{ show?: boolean }>`
  width: 200px;
  ${({ show }) => !show && `opacity: 0; margin-left: -200px; visibility: hidden;`};
  position: -webkit-sticky;
  position: sticky;
  top: ${({ theme }) => theme.bannerHeight + theme.headerHeight + 32}px;
  align-self: flex-start;
  white-space: nowrap;
  transition: all 0.25s ease-in-out;
  position: relative;
  top: 0;
  width: 100%;
  opacity: 1;
  margin-left: 0;
  visibility: visible;
`;

const SidebarItem = styled(Caption)<{
  active?: boolean;
  last?: boolean;
}>`
  display: block;
  margin-top: 8px;
  cursor: pointer;
  text-align: center;
  max-width: 84px;
  min-height: 42px;

  white-space: normal;
  word-break: break-word;

  ${({ active }) => active && S3Styles}
  &:hover {
    text-decoration: underline;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    max-width: 140px;
    min-height: unset;
  }
`;

const ImageWrapper = styled.div`
  width: 84px;
  height: 84px;

  border: 1px solid ${({ theme }) => theme.colors.defaultCreatorLogoBorder};
  background-color: ${({ theme }) => utils.hexToRGBA(theme.colors.cloud, theme.alpha.light)};
  border-radius: 0px 12px 12px 12px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    width: 140px;
    height: 140px;
  }
`;

const StyledImage = styled(Image).attrs({ draggable: false })`
  user-drag: none;
  -webkit-user-drag: none;
  pointer-events: auto;
`;

const StyledLink = styled(Link).attrs({ draggable: false })``;

const HyperLink = styled.a.attrs({ draggable: false })``;

const SidebarItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0px 5px;
  height: 100%;

  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;

  &:first-child {
    margin-left: 16px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin: 0px 16px;

    &:first-child {
      margin-left: 64px;
    }
  }
`;

type SidebarProps = {
  handle?: string;
  show?: boolean;
  sidebarItems: SidebarItemsType[];
};

const SubcategorySidebar: React.FC<SidebarProps> = ({ handle, show, sidebarItems }: SidebarProps) => {
  const { state } = useStore();

  const experimentFlags = getExperimentFlags();
  const isCollectionsSidebarExperimentEnabled =
    experimentFlags[experimentKeys.collectionsSidebarExperiment] === ExperimentVariant.Variant1;

  const { collections } = config;

  const renderedSidebarItems = useMemo(() => {
    if (handle === collections.liveCampaigns) {
      return sidebarItems.map((item) => {
        const links = item.links.filter(
          (link) =>
            link.handle === plushCollectionHandles.plushies ||
            link.handle === apparelCollectionHandles.apparel ||
            link.handle === collections.enamelPins ||
            link.handle === collections.vinyls,
        );
        return {
          ...item,
          links,
        };
      });
    }
    if (isPlushieCollectionHandle(handle)) {
      return sidebarItems.map((item) => {
        const links = item.links.filter((link) => isPlushieCollectionHandle(link.handle));
        return {
          ...item,
          links,
        };
      });
    }
    if (isApparelCollectionHandle(handle)) {
      return sidebarItems.map((item) => {
        const links = item.links.filter((link) => isApparelCollectionHandle(link.handle));
        return {
          ...item,
          links,
        };
      });
    }
    if (isTopPicksCollectionHandle(handle)) {
      return sidebarItems.map((item) => {
        const links = item.links.filter((link) => isTopPicksCollectionHandle(link.handle));
        return {
          ...item,
          links,
        };
      });
    }
  }, [handle, collections.liveCampaigns, collections.enamelPins, collections.vinyls, sidebarItems]);

  return isCollectionsSidebarExperimentEnabled && renderedSidebarItems ? (
    <SidebarWrapper show={show}>
      {renderedSidebarItems.map((sidebarItem) => (
        <HorizontalScroller>
          {sidebarItem.links.map((link, index) => (
            <SidebarItemWrapper>
              <StyledLink key={link.title} href={`/shop/${link.handle}`} passHref legacyBehavior>
                <HyperLink
                  onClick={() =>
                    posthog.capture('collection_subcategory_sidebar_item_click', {
                      itemTitle: link.title,
                      itemHandle: link.handle,
                      is_logged_in: isUserLoggedIn(state.user),
                    })
                  }
                >
                  <ImageWrapper>
                    <StyledImage
                      src={link.firstImage || '/assets/default_product_asset.png'}
                      width={140}
                      height={140}
                      quality={90}
                      alt="product image"
                    />
                  </ImageWrapper>
                  <SidebarItem active={link.handle === handle} last={sidebarItem.links.length - 1 === index}>
                    {link.title}
                  </SidebarItem>
                </HyperLink>
              </StyledLink>
            </SidebarItemWrapper>
          ))}
        </HorizontalScroller>
      ))}
    </SidebarWrapper>
  ) : (
    <></>
  );
};

export default SubcategorySidebar;
